import React from 'react';
import { X, ExternalLink, CheckCircle, AlertTriangle, XCircle, Shield, Activity, FileText, Lightbulb, Target, FileCheck, TrendingUp, Clock } from 'lucide-react';

const ReviewDetailModal = ({ review, onClose }) => {
  if (!review) return null;

  const aiReview = review.ai_review || {};
  const fullAiReview = review.full_ai_review || aiReview;
  const changeInfo = review.change_info || {};

  const getComplianceIcon = (status) => {
    switch (status) {
      case 'Compliant': return <CheckCircle size={20} className="text-green-400" />;
      case 'Partially_Compliant': return <AlertTriangle size={20} className="text-yellow-400" />;
      case 'Non_Compliant': return <XCircle size={20} className="text-red-400" />;
      default: return <AlertTriangle size={20} className="text-gray-400" />;
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'Low': return 'text-green-400';
      case 'Medium': return 'text-yellow-400';
      case 'High': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const Section = ({ title, icon: Icon, color = 'cyan', children }) => (
    <div className="mb-6">
      <div className="flex items-center space-x-2 mb-3">
        <Icon size={18} className={`text-${color}-accent`} />
        <h4 className={`text-sm font-semibold text-${color}-accent uppercase tracking-wider`}>{title}</h4>
      </div>
      <div className="pl-6">{children}</div>
    </div>
  );

  const Badge = ({ children, variant = 'default' }) => {
    const styles = {
      success: 'bg-green-500/20 text-green-400 border-green-500/40',
      warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      danger: 'bg-red-500/20 text-red-400 border-red-500/40',
      default: 'bg-gray-500/20 text-gray-400 border-gray-500/40',
      info: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${styles[variant]}`}>
        {children}
      </span>
    );
  };

  const ListItem = ({ children, type = 'neutral' }) => {
    const colors = {
      strength: 'text-green-400',
      gap: 'text-yellow-400',
      concern: 'text-red-400',
      neutral: 'text-gray-300',
      info: 'text-cyan-400'
    };
    return (
      <li className={`text-sm ${colors[type]} mb-1`}>• {children}</li>
    );
  };

  const BooleanIndicator = ({ value, trueLabel = 'Yes', falseLabel = 'No' }) => (
    <Badge variant={value ? 'success' : 'danger'}>
      {value ? trueLabel : falseLabel}
    </Badge>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-dark-card border border-dark-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden"
           style={{ background: '#141414', boxShadow: '0 0 60px rgba(0, 229, 255, 0.1)' }}>
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-border bg-dark-border/20">
          <div className="flex items-center space-x-3">
            {getComplianceIcon(review.compliance_status)}
            <div>
              <h3 className="text-lg font-bold text-white">{review.change_number}</h3>
              <p className="text-sm text-gray-400">{review.short_description || 'No description'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {review.sys_id && (
              <a 
                href={`https://dish.service-now.com/nav_to.do?uri=change_request.do?sys_id=${review.sys_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 px-3 py-1.5 rounded bg-cyan-accent/10 border border-cyan-accent/40 text-cyan-accent text-sm hover:bg-cyan-accent/20 transition-colors"
              >
                <ExternalLink size={14} />
                <span>ServiceNow</span>
              </a>
            )}
            <button 
              onClick={onClose}
              className="p-2 rounded hover:bg-dark-border/60 text-gray-400 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Change Info Summary */}
          {Object.keys(changeInfo).length > 0 && (
            <div className="mb-6 p-4 bg-dark-border/20 rounded-lg border border-dark-border/50">
              <h4 className="text-sm font-semibold text-cyan-accent uppercase tracking-wider mb-3">Change Information</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {changeInfo.type && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Type</p>
                    <p className="text-sm text-gray-300">{changeInfo.type}</p>
                  </div>
                )}
                {changeInfo.state && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">State</p>
                    <p className="text-sm text-gray-300">{changeInfo.state}</p>
                  </div>
                )}
                {changeInfo.priority && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Priority</p>
                    <p className="text-sm text-gray-300">{changeInfo.priority}</p>
                  </div>
                )}
                {changeInfo.risk && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">ServiceNow Risk</p>
                    <span className={`text-sm font-semibold ${getRiskColor(changeInfo.risk)}`}>
                      {changeInfo.risk}
                    </span>
                  </div>
                )}
                {changeInfo.start_date && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Start Date</p>
                    <p className="text-sm text-gray-300">{new Date(changeInfo.start_date).toLocaleString()}</p>
                  </div>
                )}
                {changeInfo.end_date && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">End Date</p>
                    <p className="text-sm text-gray-300">{new Date(changeInfo.end_date).toLocaleString()}</p>
                  </div>
                )}
                {changeInfo.assignment_group && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 uppercase mb-1">Assignment Group</p>
                    <p className="text-sm text-gray-300">{changeInfo.assignment_group}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-dark-border/20 rounded-lg border border-dark-border/50">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Compliance</p>
              <Badge variant={review.compliance_status === 'Compliant' ? 'success' : review.compliance_status === 'Non_Compliant' ? 'danger' : 'warning'}>
                {(review.compliance_status || 'Unknown').replace(/_/g, ' ')}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">AI Risk Level</p>
              <span className={`text-sm font-semibold ${getRiskColor(review.risk_level)}`}>
                {review.risk_level || 'Unknown'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Recommendation</p>
              <Badge variant={review.approval_recommendation === 'Ready_For_Approval' ? 'success' : review.approval_recommendation === 'Escalate_To_CAB' ? 'danger' : 'warning'}>
                {(review.approval_recommendation || 'Unknown').replace(/_/g, ' ')}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Posted to SN</p>
              <Badge variant={review.posted_to_servicenow ? 'success' : 'default'}>
                {review.posted_to_servicenow ? 'Yes' : 'No'}
              </Badge>
            </div>
          </div>

          {/* Review Summary */}
          {(() => {
            const reviewSummary = fullAiReview.review_summary || aiReview.review_summary;
            if (!reviewSummary) return null;
            
            // Safety check: never render objects directly
            const isString = typeof reviewSummary === 'string';
            const isObject = reviewSummary && typeof reviewSummary === 'object' && !Array.isArray(reviewSummary);
            
            return (
              <Section title="Review Summary" icon={FileText}>
                <div className="space-y-4">
                  {/* Handle string format */}
                  {isString && (
                    <p className="text-sm text-gray-300 leading-relaxed">{reviewSummary}</p>
                  )}
                  
                  {/* Handle object format (DynamoDB structure) */}
                  {isObject && (
                    <>
                      {reviewSummary.change_purpose && typeof reviewSummary.change_purpose === 'string' && (
                        <div>
                          <p className="text-xs text-cyan-accent font-semibold uppercase mb-1">Change Purpose</p>
                          <p className="text-sm text-gray-300 leading-relaxed">
                            {reviewSummary.change_purpose}
                          </p>
                        </div>
                      )}
                      {reviewSummary.approver_focus && typeof reviewSummary.approver_focus === 'string' && (
                        <div>
                          <p className="text-xs text-purple-accent font-semibold uppercase mb-1">Approver Focus</p>
                          <p className="text-sm text-gray-300 leading-relaxed">
                            {reviewSummary.approver_focus}
                          </p>
                        </div>
                      )}
                      {Array.isArray(reviewSummary.key_points) && reviewSummary.key_points.length > 0 && (
                        <div>
                          <p className="text-xs text-orange-accent font-semibold uppercase mb-2">Key Points</p>
                          <ul className="space-y-2">
                            {reviewSummary.key_points.map((point, i) => {
                              // Ensure point is a string before rendering
                              const pointText = typeof point === 'string' ? point : String(point || '');
                              return (
                                <li key={i} className="text-sm text-gray-300 flex items-start space-x-2">
                                  <span className="text-orange-accent mt-1">•</span>
                                  <span>{pointText}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Section>
            );
          })()}

          {/* Approval Guidance */}
          {(fullAiReview.approval_guidance || aiReview.approval_guidance) && (
            <Section title="Approval Guidance" icon={Shield} color="purple">
              <div className="space-y-3">
                {(fullAiReview.approval_guidance?.recommendation || aiReview.approval_guidance?.recommendation) && (
                  <p className="text-sm text-gray-300">
                    <span className="text-purple-accent font-medium">Recommendation:</span> {fullAiReview.approval_guidance?.recommendation || aiReview.approval_guidance?.recommendation}
                  </p>
                )}
                {(fullAiReview.approval_guidance?.confidence || aiReview.approval_guidance?.confidence) && (
                  <p className="text-sm text-gray-300">
                    <span className="text-purple-accent font-medium">Confidence:</span> {fullAiReview.approval_guidance?.confidence || aiReview.approval_guidance?.confidence}
                  </p>
                )}
                {(fullAiReview.approval_guidance?.reasoning || aiReview.approval_guidance?.reasoning) && (
                  <p className="text-sm text-gray-300">
                    <span className="text-purple-accent font-medium">Reasoning:</span> {fullAiReview.approval_guidance?.reasoning || aiReview.approval_guidance?.reasoning}
                  </p>
                )}
                
                {/* Blocking Issues */}
                {(fullAiReview.approval_guidance?.blocking_issues?.length > 0 || aiReview.blocking_issues?.length > 0) && (
                  <div className="mt-3 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <p className="text-xs text-red-400 font-semibold uppercase mb-2">Blocking Issues</p>
                    <ul className="space-y-1">
                      {(fullAiReview.approval_guidance?.blocking_issues || aiReview.blocking_issues || []).map((issue, i) => (
                        <ListItem key={i} type="concern">{issue}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Conditions for Approval */}
                {(fullAiReview.approval_guidance?.conditions_for_approval?.length > 0 || aiReview.conditions_for_approval?.length > 0) && (
                  <div className="mt-3 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                    <p className="text-xs text-yellow-400 font-semibold uppercase mb-2">Conditions for Approval</p>
                    <ul className="space-y-1">
                      {(fullAiReview.approval_guidance?.conditions_for_approval || aiReview.conditions_for_approval || []).map((condition, i) => (
                        <ListItem key={i} type="gap">{condition}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Post Approval Actions */}
                {(fullAiReview.approval_guidance?.post_approval_actions?.length > 0 || aiReview.post_approval_actions?.length > 0) && (
                  <div className="mt-3 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                    <p className="text-xs text-cyan-400 font-semibold uppercase mb-2">Post-Approval Actions</p>
                    <ul className="space-y-1">
                      {(fullAiReview.approval_guidance?.post_approval_actions || aiReview.post_approval_actions || []).map((action, i) => (
                        <ListItem key={i} type="info">{action}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Findings */}
          {(fullAiReview.findings || aiReview.findings) && (
            <Section title="Findings" icon={AlertTriangle} color="orange">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Strengths */}
                {(fullAiReview.findings?.strengths?.length > 0 || aiReview.findings?.strengths?.length > 0 || aiReview.strengths?.length > 0) && (
                  <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                    <p className="text-xs text-green-400 font-semibold uppercase mb-2">Strengths</p>
                    <ul className="space-y-1">
                      {(fullAiReview.findings?.strengths || aiReview.findings?.strengths || aiReview.strengths || []).map((s, i) => (
                        <ListItem key={i} type="strength">{s}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Gaps */}
                {(fullAiReview.findings?.gaps?.length > 0 || aiReview.findings?.gaps?.length > 0 || aiReview.gaps?.length > 0) && (
                  <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                    <p className="text-xs text-yellow-400 font-semibold uppercase mb-2">Gaps</p>
                    <ul className="space-y-1">
                      {(fullAiReview.findings?.gaps || aiReview.findings?.gaps || aiReview.gaps || []).map((g, i) => (
                        <ListItem key={i} type="gap">{g}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Policy Concerns */}
                {(fullAiReview.findings?.policy_concerns?.length > 0 || aiReview.findings?.policy_concerns?.length > 0 || aiReview.policy_concerns?.length > 0) && (
                  <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                    <p className="text-xs text-red-400 font-semibold uppercase mb-2">Policy Concerns</p>
                    <ul className="space-y-1">
                      {(fullAiReview.findings?.policy_concerns || aiReview.findings?.policy_concerns || aiReview.policy_concerns || []).map((c, i) => (
                        <ListItem key={i} type="concern">{c}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* CI Validation */}
          {(fullAiReview.ci_validation || aiReview.ci_validation) && (
            <Section title="CI Validation" icon={CheckCircle} color="cyan">
              <div className="text-sm text-gray-300 space-y-2">
                {(fullAiReview.ci_validation?.status || aiReview.ci_validation?.status) && (
                  <p><span className="text-cyan-accent font-medium">Status:</span> {fullAiReview.ci_validation?.status || aiReview.ci_validation?.status}</p>
                )}
                {(fullAiReview.ci_validation?.cis_attached_count !== undefined) && (
                  <p><span className="text-cyan-accent font-medium">CIs Attached:</span> {fullAiReview.ci_validation.cis_attached_count}</p>
                )}
                {(fullAiReview.ci_validation?.systems_in_description?.length > 0) && (
                  <div>
                    <p className="text-cyan-accent font-medium mb-1">Systems in Description:</p>
                    <ul className="pl-4 space-y-1">
                      {fullAiReview.ci_validation.systems_in_description.map((sys, i) => (
                        <ListItem key={i} type="info">{sys}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
                {(fullAiReview.ci_validation?.systems_without_cis?.length > 0) && (
                  <div>
                    <p className="text-yellow-400 font-medium mb-1">Systems Without CIs:</p>
                    <ul className="pl-4 space-y-1">
                      {fullAiReview.ci_validation.systems_without_cis.map((sys, i) => (
                        <ListItem key={i} type="gap">{sys}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
                {(fullAiReview.ci_validation?.issues?.length > 0) && (
                  <div>
                    <p className="text-red-400 font-medium mb-1">Issues:</p>
                    <ul className="pl-4 space-y-1">
                      {fullAiReview.ci_validation.issues.map((issue, i) => (
                        <ListItem key={i} type="concern">{issue}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Dynatrace Monitoring */}
          {(fullAiReview.dynatrace_validation || aiReview.dynatrace_validation) && (
            <Section title="Dynatrace Monitoring" icon={Activity} color="purple">
              <div className="text-sm text-gray-300 space-y-2">
                {(fullAiReview.dynatrace_validation?.coverage_status || aiReview.dynatrace_validation?.coverage_status) && (
                  <p><span className="text-purple-accent font-medium">Coverage Status:</span> {fullAiReview.dynatrace_validation?.coverage_status || aiReview.dynatrace_validation?.coverage_status}</p>
                )}
                {(fullAiReview.dynatrace_validation?.validation_performed !== undefined) && (
                  <p>
                    <span className="text-purple-accent font-medium">Validation Performed:</span>{' '}
                    <BooleanIndicator value={fullAiReview.dynatrace_validation.validation_performed} />
                  </p>
                )}
                {(fullAiReview.dynatrace_validation?.systems_verified?.length > 0) && (
                  <div>
                    <p className="text-green-400 font-medium mb-1">Systems Verified:</p>
                    <ul className="pl-4 space-y-1">
                      {fullAiReview.dynatrace_validation.systems_verified.map((sys, i) => (
                        <ListItem key={i} type="strength">{sys}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
                {(fullAiReview.dynatrace_validation?.systems_not_found?.length > 0) && (
                  <div>
                    <p className="text-yellow-400 font-medium mb-1">Systems Not Found:</p>
                    <ul className="pl-4 space-y-1">
                      {fullAiReview.dynatrace_validation.systems_not_found.map((sys, i) => (
                        <ListItem key={i} type="gap">{sys}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
                {(fullAiReview.dynatrace_validation?.monitoring_concerns?.length > 0) && (
                  <div>
                    <p className="text-red-400 font-medium mb-1">Monitoring Concerns:</p>
                    <ul className="pl-4 space-y-1">
                      {fullAiReview.dynatrace_validation.monitoring_concerns.map((concern, i) => (
                        <ListItem key={i} type="concern">{concern}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Risk Assessment */}
          {fullAiReview.risk_assessment && (
            <Section title="Risk Assessment" icon={Shield} color="orange">
              <div className="space-y-3">
                {fullAiReview.risk_assessment.assigned_level && (
                  <p className="text-sm text-gray-300">
                    <span className="text-orange-accent font-medium">Assigned Level:</span>{' '}
                    <span className={getRiskColor(fullAiReview.risk_assessment.assigned_level)}>
                      {fullAiReview.risk_assessment.assigned_level}
                    </span>
                  </p>
                )}
                {fullAiReview.risk_assessment.reasoning && (
                  <p className="text-sm text-gray-300">
                    <span className="text-orange-accent font-medium">Reasoning:</span> {fullAiReview.risk_assessment.reasoning}
                  </p>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  {fullAiReview.risk_assessment.risk_factors?.length > 0 && (
                    <div className="p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <p className="text-xs text-red-400 font-semibold uppercase mb-2">Risk Factors</p>
                      <ul className="space-y-1">
                        {fullAiReview.risk_assessment.risk_factors.map((factor, i) => (
                          <ListItem key={i} type="concern">{factor}</ListItem>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {fullAiReview.risk_assessment.mitigations_present?.length > 0 && (
                    <div className="p-3 bg-green-500/5 border border-green-500/20 rounded-lg">
                      <p className="text-xs text-green-400 font-semibold uppercase mb-2">Mitigations Present</p>
                      <ul className="space-y-1">
                        {fullAiReview.risk_assessment.mitigations_present.map((mit, i) => (
                          <ListItem key={i} type="strength">{mit}</ListItem>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {fullAiReview.risk_assessment.mitigations_missing?.length > 0 && (
                    <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                      <p className="text-xs text-yellow-400 font-semibold uppercase mb-2">Mitigations Missing</p>
                      <ul className="space-y-1">
                        {fullAiReview.risk_assessment.mitigations_missing.map((mit, i) => (
                          <ListItem key={i} type="gap">{mit}</ListItem>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </Section>
          )}

          {/* Technical Assessment */}
          {fullAiReview.technical_assessment && (
            <Section title="Technical Assessment" icon={Target} color="cyan">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                {fullAiReview.technical_assessment.approach_sound !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Approach Sound</p>
                    <BooleanIndicator value={fullAiReview.technical_assessment.approach_sound} />
                  </div>
                )}
                {fullAiReview.technical_assessment.dependencies_identified !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Dependencies ID'd</p>
                    <BooleanIndicator value={fullAiReview.technical_assessment.dependencies_identified} />
                  </div>
                )}
                {fullAiReview.technical_assessment.downtime_realistic !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Downtime Realistic</p>
                    <BooleanIndicator value={fullAiReview.technical_assessment.downtime_realistic} />
                  </div>
                )}
                {fullAiReview.technical_assessment.duration_realistic !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Duration Realistic</p>
                    <BooleanIndicator value={fullAiReview.technical_assessment.duration_realistic} />
                  </div>
                )}
              </div>
              {fullAiReview.technical_assessment.observations && (
                <p className="text-sm text-gray-300 mt-2">
                  <span className="text-cyan-accent font-medium">Observations:</span> {fullAiReview.technical_assessment.observations}
                </p>
              )}
            </Section>
          )}

          {/* Documentation Assessment */}
          {fullAiReview.documentation_assessment && (
            <Section title="Documentation Assessment" icon={FileCheck} color="purple">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                {fullAiReview.documentation_assessment.description_quality && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Description Quality</p>
                    <Badge variant={
                      fullAiReview.documentation_assessment.description_quality === 'Good' ? 'success' :
                      fullAiReview.documentation_assessment.description_quality === 'Needs_Improvement' ? 'warning' : 'danger'
                    }>
                      {fullAiReview.documentation_assessment.description_quality.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                )}
                {fullAiReview.documentation_assessment.justification_adequate !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Justification</p>
                    <BooleanIndicator value={fullAiReview.documentation_assessment.justification_adequate} trueLabel="Adequate" falseLabel="Inadequate" />
                  </div>
                )}
                {fullAiReview.documentation_assessment.impact_analysis_adequate !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Impact Analysis</p>
                    <BooleanIndicator value={fullAiReview.documentation_assessment.impact_analysis_adequate} trueLabel="Adequate" falseLabel="Inadequate" />
                  </div>
                )}
                {fullAiReview.documentation_assessment.implementation_plan_adequate !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Implementation Plan</p>
                    <BooleanIndicator value={fullAiReview.documentation_assessment.implementation_plan_adequate} trueLabel="Adequate" falseLabel="Inadequate" />
                  </div>
                )}
                {fullAiReview.documentation_assessment.test_plan_adequate !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Test Plan</p>
                    <BooleanIndicator value={fullAiReview.documentation_assessment.test_plan_adequate} trueLabel="Adequate" falseLabel="Inadequate" />
                  </div>
                )}
                {fullAiReview.documentation_assessment.backout_plan_adequate !== undefined && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase mb-1">Backout Plan</p>
                    <BooleanIndicator value={fullAiReview.documentation_assessment.backout_plan_adequate} trueLabel="Adequate" falseLabel="Inadequate" />
                  </div>
                )}
              </div>
              {fullAiReview.documentation_assessment.observations && (
                <p className="text-sm text-gray-300 mt-2">
                  <span className="text-purple-accent font-medium">Observations:</span> {fullAiReview.documentation_assessment.observations}
                </p>
              )}
            </Section>
          )}

          {/* Implementation Readiness */}
          {fullAiReview.implementation_readiness && (
            <Section title="Implementation Readiness" icon={TrendingUp} color="orange">
              <div className="mb-3">
                {fullAiReview.implementation_readiness.ready_to_implement !== undefined && (
                  <p className="text-sm text-gray-300">
                    <span className="text-orange-accent font-medium">Ready to Implement:</span>{' '}
                    <BooleanIndicator value={fullAiReview.implementation_readiness.ready_to_implement} />
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fullAiReview.implementation_readiness.readiness_gaps?.length > 0 && (
                  <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                    <p className="text-xs text-yellow-400 font-semibold uppercase mb-2">Readiness Gaps</p>
                    <ul className="space-y-1">
                      {fullAiReview.implementation_readiness.readiness_gaps.map((gap, i) => (
                        <ListItem key={i} type="gap">{gap}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
                
                {fullAiReview.implementation_readiness.recommendations_before_implementation?.length > 0 && (
                  <div className="p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                    <p className="text-xs text-cyan-400 font-semibold uppercase mb-2">Recommendations Before Implementation</p>
                    <ul className="space-y-1">
                      {fullAiReview.implementation_readiness.recommendations_before_implementation.map((rec, i) => (
                        <ListItem key={i} type="info">{rec}</ListItem>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Recommendations */}
          {(fullAiReview.approver_recommendations?.length > 0 || aiReview.approver_recommendations?.length > 0 || aiReview.recommendations?.length > 0) && (
            <Section title="Approver Recommendations" icon={Lightbulb} color="orange">
              <div className="space-y-3">
                {(fullAiReview.approver_recommendations || aiReview.approver_recommendations || aiReview.recommendations || []).map((rec, i) => {
                  // Handle both structured (object) and simple (string) formats
                  if (typeof rec === 'string') {
                    return (
                      <div key={i} className="p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                        <p className="text-sm text-gray-300">{rec}</p>
                      </div>
                    );
                  } else {
                    // Structured format with category, finding, action_needed, impact, priority
                    const priorityColor = rec.priority === 'High' ? 'text-red-400' : rec.priority === 'Medium' ? 'text-yellow-400' : 'text-green-400';
                    return (
                      <div key={i} className="p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <Badge variant="info">{rec.category}</Badge>
                          <span className={`text-xs font-semibold ${priorityColor}`}>{rec.priority} Priority</span>
                        </div>
                        {rec.finding && (
                          <p className="text-sm text-gray-300 mb-2">
                            <span className="text-orange-accent font-medium">Finding:</span> {rec.finding}
                          </p>
                        )}
                        {rec.action_needed && (
                          <p className="text-sm text-gray-300 mb-2">
                            <span className="text-orange-accent font-medium">Action Needed:</span> {rec.action_needed}
                          </p>
                        )}
                        {rec.impact && (
                          <p className="text-sm text-gray-400 text-xs">
                            <span className="font-medium">Impact:</span> {rec.impact}
                          </p>
                        )}
                      </div>
                    );
                  }
                })}
              </div>
            </Section>
          )}

          {/* Key Concerns */}
          {(fullAiReview.key_concerns_for_approvers?.length > 0 || aiReview.key_concerns_for_approvers?.length > 0 || aiReview.key_concerns?.length > 0) && (
            <Section title="Key Concerns for Approvers" icon={AlertTriangle} color="orange">
              <ul className="space-y-2">
                {(fullAiReview.key_concerns_for_approvers || aiReview.key_concerns_for_approvers || aiReview.key_concerns || []).map((concern, i) => (
                  <li key={i} className="text-sm text-orange-400 flex items-start space-x-2 p-2 bg-orange-500/5 border border-orange-500/20 rounded">
                    <span className="mt-1">⚠</span>
                    <span>{concern}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Metadata */}
          <div className="mt-6 pt-4 border-t border-dark-border/50">
            <div className="flex items-center space-x-2 mb-3">
              <Clock size={18} className="text-gray-500" />
              <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Review Metadata</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <p className="text-gray-500 uppercase mb-1">Requester</p>
                <p className="text-gray-400">{review.requester || changeInfo.requester || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 uppercase mb-1">Reviewed At</p>
                <p className="text-gray-400">{review.review_timestamp ? new Date(review.review_timestamp).toLocaleString() : '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 uppercase mb-1">Model Used</p>
                <p className="text-gray-400">{fullAiReview.model_used || aiReview.model_used || '-'}</p>
              </div>
              <div>
                <p className="text-gray-500 uppercase mb-1">Review Type</p>
                <p className="text-gray-400">{fullAiReview.metadata?.review_type || '-'}</p>
              </div>
              {fullAiReview.metadata?.analysis_notes && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-gray-500 uppercase mb-1">Analysis Notes</p>
                  <p className="text-gray-400">{fullAiReview.metadata.analysis_notes}</p>
                </div>
              )}
              {fullAiReview.metadata?.policies_referenced?.length > 0 && (
                <div className="col-span-2 md:col-span-4">
                  <p className="text-gray-500 uppercase mb-1">Policies Referenced</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {fullAiReview.metadata.policies_referenced.map((policy, i) => (
                      <Badge key={i} variant="info">{policy}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="col-span-2 md:col-span-4">
                <p className="text-gray-500 uppercase mb-1">Sys ID</p>
                <p className="text-gray-400 font-mono text-xs">{review.sys_id || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetailModal;
